import React, { useCallback, useMemo, useState, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import EventLabel from '../../components/EventLabel/EventLabel';
import { Dialog, Transition } from '@headlessui/react';
import { basedAxios } from '../../store/Store';
import cx from 'classnames';
import './ticket.css';

interface TicketsProps {
  orders: any;
  activePersonNo: number;
  breakouts: any;
  updateOrderRow: any;
  preview: boolean;
}

type Ticket = {
  name: string;
  used: boolean;
  id: number;
};

function Tickets(props: any) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const { orders, activePersonNo, breakouts, updateOrderRow, preview } = props;

  const tickets = useMemo(() => {
    const orderRows = orders?.[activePersonNo]?.orderRow ?? [];
    const ticketSources = breakouts?.filter((breakout: any) => breakout.ticket && orderRows?.some((orderRow: any) => orderRow?.eventId === breakout?.eventId));

    return ticketSources?.flatMap((source: any) => {
      const breakout = breakouts?.find((breakout: any) => breakout.eventId === source?.eventId);
      const orderRow = orderRows?.find((orderRow: any) => orderRow.eventId === source?.eventId);

      const name = breakout?.name || 'No name';
      const quantity = orderRow.quantity;
      const used = orderRow.usedTickets ?? 0;
      const result = [];

      for (let i = 0; i < quantity; i++) {
        const isUsed = i < used;
        result.push({
          id: orderRow?.orderRowId,
          name, // You need to define 'name' somewhere in your code
          used: isUsed,
        });
      }

      return result.sort((a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1));
    });
  }, [orders, activePersonNo, breakouts]);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedTicket(null);
  };
  const handleOpen = () => setIsOpen(true);

  const confirmUsage = (id: number) => {
    handleOpen();
    setSelectedTicket(id);
  };

  const saveOrderRow = async (orderRow: any) => {
    try {
      await basedAxios.put(`./orderRow/useticket/${orderRow.orderRowId}`, orderRow);
    } catch (error) {
      console.error(error);
    }
  };

  const useTicket = useCallback(() => {
    const orderRow = orders?.[activePersonNo]?.orderRow?.find((orderRow: any) => orderRow.orderRowId === selectedTicket);

    if (orderRow.usedTickets === orderRow.quantity) return;

    setConfirmed(true);

    const updatedOrderRow = {
      ...orderRow,
      usedTickets: (orderRow.usedTickets ?? 0) + 1,
    };

    updateOrderRow(updatedOrderRow);

    setSelectedTicket(null);
    setIsOpen(false);

    saveOrderRow(updatedOrderRow);

    setConfirmed(false);
  }, [orders, selectedTicket]);

  if (tickets?.length === 0) return null;

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-fit max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center">
                    <EventLabel name="UseTicket" groupName="CMS.Tickets" />
                  </Dialog.Title>

                  <div className="mt-4 flex gap-x-2 justify-center">
                    <button
                      className="inline-flex justify-center rounded-md border border-transparent bg-neutral-200 px-4 py-2 text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 "
                      onClick={handleClose}
                    >
                      <EventLabel name="Cancel" groupName="CMS.Tickets" />
                    </button>
                    <button
                      className="inline-flex justify-center rounded-md border border-transparent bg-[teal] px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 "
                      onClick={useTicket}
                    >
                      {confirmed ? (
                        <>
                          <i className="ri ri-check-line mr-1" /> <EventLabel name="TicketIsUsed" groupName="CMS.Tickets" />{' '}
                        </>
                      ) : (
                        <EventLabel name="Use" groupName="CMS.Tickets" />
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <div className="w-full">
        <h3 className="mb-2">
          <EventLabel name="Tickets" groupName="CMS.Tickets" />
        </h3>
        <p>
          <EventLabel name="Quantity" groupName="CMS.Tickets" />
          {': '} {tickets?.filter((ticket: Ticket) => !ticket.used).length}
        </p>
        <br />
        <div className='grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-x-4'>
          {tickets?.map(({ name, used, id }: Ticket) => (
            // <div
            //   className={cx(
            //     'rounded-md mb-2 border border-neutral-300 py-2 px-4 w-full justify-between flex gap-x-12 items-center',
            //     used ? 'opacity-50' : 'opacity-100'
            //   )}
            // >
            //   <div>
            //     <div className="flex items-center w-full gap-x-2">
            //       <i className="ri ri-ticket-line ri-md" />
            //       <span className="text-lg">{name}</span>
            //     </div>
            //   </div>
            //   <button disabled={used} className="cms-button w-fit" style={{ fontSize: 14, width: 'fit-content' }} onClick={() => confirmUsage(id)}>
            //     {used ? <EventLabel name="TicketIsUsed" groupName="CMS.Tickets" /> : <EventLabel name="UseTicket" groupName="CMS.Tickets" />}
            //   </button>
            // </div>

            <div className={cx('ticket xl:grid-span-4 lg:grid-span-2 grid-span-1', used ? 'opacity-50' : 'opacity-100')}>
              <div className="top --flex-column">
                <div className="bandname -bold">{name}</div>
              </div>
              <div className="rip ">
                <hr />
              </div>
              <div className="bottom --flex-row-j!sb">
                <button disabled={used} className="cms-button w-fit" style={{ fontSize: 14, width: 'fit-content' }} onClick={() => confirmUsage(id)}>
                  {used ? <EventLabel name="TicketIsUsed" groupName="CMS.Tickets" /> : <EventLabel name="UseTicket" groupName="CMS.Tickets" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default observer(Tickets);
